import React, { useMemo, useState } from "react";
import { OrganizationUserVM, OrganizationUserRolesEnum } from "@propertelligent/client-api";
import { GetImages } from "../../../utils/GetImages";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../../newComponents/emptystate";
import homeIconBlue from "../../../assets/homeIconBlue.svg";
import apiHelper from "../../../utils/apiHelper";
import { OrganizationUserService } from "@propertelligent/client-api";
import UtilHelper from "../../../utils/UtilHelper";
import { usePermissions } from "../../../hooks/usePermissions";

interface DataHookState {
  rows: OrganizationUserVM[];
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
  handleFullRefresh: () => void;
}

interface UserCardItemProps {
  className?: string;
  dataHookState: DataHookState;
  onEditUser?: (user: OrganizationUserVM) => void;
  hasAnyUsers?: boolean;
}

const UserCardItem = ({
  className = "",
  dataHookState,
  onEditUser,
  hasAnyUsers = true,
}: UserCardItemProps) => {
  const { rows, loading, hasMore, fetchData, currentPage, handleFullRefresh } = dataHookState;
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const check = usePermissions();
  const canWrite = check.canWrite("ManageUsers");

  const menuItemsMap = useMemo(() => {
    return rows.reduce((acc, listingData) => {
      const items = [];
      if (canWrite) {
        items.push(
          {
            icon: editIcon,
            label: "Edit",
            action: () => onEditUser?.(listingData),
            isDivider: false,
            isDestructive: false,
          },
          { isDivider: true },
          {
            icon: deleteIcon,
            label: "Delete",
            action: () => setShowConfirmation(String(listingData.id)),
            isDivider: false,
            isDestructive: true,
          }
        );
      }
      acc[listingData.id || 0] = items;
      return acc;
    }, {} as Record<string | number, any[]>);
  }, [rows, onEditUser, canWrite]);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const handleDeleteUser = (userId: any, rowId: any) => {
    if (deletingUserId) return;

    setDeletingUserId(String(rowId));
    apiHelper(OrganizationUserService.deleteApiOrganizationUser, {
      title:"User Deleted",
      successMessage: "User Deleted Successfully",
      showNotification: true,
      successIcon: <img src={GetImages.toastUserIcon} alt="success" width="20" height="20" />
    }, userId)
      .then((res) => {
        if (res?.data?.success) {
          // A delete changes the total count, so refresh the pill-count baseline too (not just
          // refetch the current page) - otherwise a pill can be left showing a stale count
          // until the page is manually reloaded.
          handleFullRefresh();
          setShowConfirmation(null);
        }
      })
      .catch((err) => {
        console.error("Error deleting user:", err);
        setShowConfirmation(null);
      })
      .finally(() => {
        setDeletingUserId(null);
      });
  };



  return (
    <div className="infinite-scroll-container">
      {loading && rows.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : rows.length === 0 && !loading ? (
        <EmptyState
          icon={GetImages.emptyStateUsers}
          heading={hasAnyUsers ? "No Users Found" : "Add Your First User"}
          buttonLabel={hasAnyUsers ? "Add User" : "Add User"}
          isButton={hasAnyUsers ? false : true}
          onButtonClick={() => onEditUser?.({} as OrganizationUserVM)}
        />
      ) : (
        <>
          {rows.map((listingData) => (
            <div key={listingData.id} className={`listing-card ${className} user-listing-card`}>
              <div className="listing-card-header">
                <div className="tenant-avatar">
                  <div className="tenant-initials">{listingData?.initials || "NA"}</div>
                </div>
                <div className="listing-details">
                  <div className="listing-info-container">
                    <div className="listing-info">
                      <h3 className="listing-address">{listingData.fullName}</h3>
                      <p className="listing-type">
                        {listingData?.formatted?.OrganizationUserTypeId || "Unknown"}
                      </p>
                      <p className="listing-type">
                        {listingData?.email} • {listingData?.formatted?.PhoneNumber}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Status Badge */}
                <div className="listing-progress-wrapper">
                  {listingData?.id && (
                    <span
                      className={`listing-status-badge mb-0 ${
                        listingData?.formatted?.UserStatus === "Invitation Pending"
                          ? "pending"
                          : listingData?.formatted?.UserStatus === "Active"
                          ? "active"
                          : listingData?.formatted?.UserStatus === "Inactive"
                          ? "inactive"
                          : ""
                      }`}
                    >
                      {listingData?.formatted?.UserStatus}
                    </span>
                  )}
                </div>
                {canWrite && menuItemsMap[listingData.id || 0]?.length > 0 && (
                  <ActionMenu
                    trigger={<img src={dropdownIcon} alt="Menu" width={24} height={24} className="iconStyleWithWhiteFilter" />}
                    menuItems={menuItemsMap[listingData.id || 0]}
                    Id={listingData.id || 0}
                  />
                )}
              </div>

              {showConfirmation === String(listingData.id) && (
                <ConfirmationPopup
                  isOpen={true}
                  title="Delete User"
                  message={`Do you want to delete <strong>${listingData.fullName}</strong>?`}
                  primaryButtonLabel="Delete"
                  secondaryButtonLabel="Cancel"
                  onPrimaryAction={() => handleDeleteUser(listingData.organizationUserId, listingData.id)}
                  onSecondaryAction={() => setShowConfirmation(null)}
                  onClose={() => setShowConfirmation(null)}
                  isPrimaryDestructive={true}
                  isLoading={deletingUserId === String(listingData.id)}
                  loadingLabel="Deleting..."
                />
              )}
            </div>
          ))}
          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner myClassName="Icon40" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserCardItem;